import styles from './programme-workout-popup.module.scss'
import HeartIconSelected from '../../asset/image/programmes/heart-icon-selected.png'
import HeartIcon from '../../asset/image/programmes/heart-icon.png'
import { useAddToFavorites, useFavoriteWorkouts, useRemoveFavoriteWorkout } from '../../hook/use-favorite-workouts'
import { useWorkoutIntensities } from '../../hook/use-workouts'
import { ImagePublicFileId } from '../image/image-public-file-id'
import { ClockSvgIcon, EquipmentSvgIcon, ProgrammeSpotifySvgIcon } from '../util/images'
import React, { useContext, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { SessionContext } from 'src/context/session-context'
import { Intensity, Workout } from 'src/dao/core-dao'

interface WorkoutPopupProps {
  setOnClose: React.Dispatch<React.SetStateAction<boolean>>
  selectedWorkout?: Workout
  startWorkoutLink?: string
}

export const WorkoutPopup = ({ setOnClose, selectedWorkout, startWorkoutLink }: WorkoutPopupProps): React.ReactElement => {
  const [workoutIntensity, setWorkoutIntensity] = useState<Intensity>()
  const [isFavorite, setIsFavorite] = useState<boolean>(false)

  const { data: favoriteWorkouts = [] } = useFavoriteWorkouts()
  const { data: workoutIntensities = [] } = useWorkoutIntensities()
  const addToFavoritesMutation = useAddToFavorites()
  const removeFavoriteMutation = useRemoveFavoriteWorkout()

  const selectedWorkoutId = selectedWorkout?.id ?? ''
  const selectedWorkoutIntensityId = selectedWorkout?.intensityId ?? ''

  useEffect(() => {
    if (selectedWorkout) {
      // Check if the workout is in favorites
      const workoutInList = favoriteWorkouts.find((f) => f.workout.id === selectedWorkout.id)
      if (workoutInList) {
        selectedWorkout.favoriteId = workoutInList.id
        setIsFavorite(true)
      } else {
        setIsFavorite(false)
      }

      // Get workout intensity
      if (selectedWorkoutIntensityId) {
        setWorkoutIntensity(getIntensity(workoutIntensities, selectedWorkoutIntensityId))
      }
    }
  }, [selectedWorkout, selectedWorkoutIntensityId, favoriteWorkouts, workoutIntensities])

  const workoutLink = startWorkoutLink

  const addToFavorites2 = (): void => {
    if (selectedWorkout) {
      addToFavoritesMutation.mutate(
        { workoutId: selectedWorkoutId },
        {
          onSuccess: (data) => {
            selectedWorkout.favoriteId = data.id
            setIsFavorite(true)
          },
        },
      )
    }
  }

  const removeFromFavorites2 = (): void => {
    if (selectedWorkout?.favoriteId) {
      removeFavoriteMutation.mutate(
        { id: selectedWorkout.favoriteId },
        {
          onSuccess: () => {
            selectedWorkout.favoriteId = undefined
            setIsFavorite(false)
          },
        },
      )
    }
  }

  return (
    <Modal
      className={styles.workoutPopup}
      contentClassName={styles.workoutPopupContent}
      show={!!selectedWorkoutId}
      onHide={() => setOnClose(false)}
      centered
    >
      {selectedWorkout && (
        <WorkoutPopupBody
          workout={selectedWorkout}
          startWorkoutLink={workoutLink}
          intensity={workoutIntensity}
          addToFavorites={addToFavorites2}
          removeFromFavorites={removeFromFavorites2}
          isFavorite={isFavorite}
        />
      )}
    </Modal>
  )
}

export const WorkoutPopupBody = ({
  workout,
  startWorkoutLink,
  intensity,
  addToFavorites,
  removeFromFavorites,
  isFavorite,
}: {
  workout: Workout
  startWorkoutLink?: string
  intensity?: Intensity
  addToFavorites: () => void
  removeFromFavorites: () => void
  isFavorite: boolean
}): React.ReactElement => {
  const { getFirstTrainerFromWorkout, renderDuration, renderEquipmentPieces, hasActiveSubscription, toggleSubscriptionModal } =
    useContext(SessionContext)
  const navigate = useNavigate()

  const { title: workoutTitle, description: workoutDescription, equipment, favoriteId } = workout

  const trainer = getFirstTrainerFromWorkout(workout)?.firstName
  const duration = renderDuration(workout.durationId)
  const title = workoutTitle ?? ''
  const description = workoutDescription ?? ''

  const equipmentPiecesLabel = renderEquipmentPieces(equipment)

  const handleFavoritePress = (): void => {
    if (favoriteId) {
      removeFromFavorites()
    } else {
      addToFavorites()
    }
  }

  const handleStartWorkout = (): void => {
    if (!hasActiveSubscription) {
      toggleSubscriptionModal()
      return
    }
    if (startWorkoutLink) {
      navigate(startWorkoutLink)
    }
  }

  return (
    <div className={styles.workoutPopupContainer}>
      <div className={styles.imageContainer}>
        <ImagePublicFileId fileId={workout?.imagePublicFileId} className={styles.coverImage} />
        <div>
          {title && <h2>{title}</h2>}
          {trainer && <h3>{trainer}</h3>}
        </div>
      </div>
      <div className={styles.workoutInfo}>
        {intensity && (
          <div className={`${styles.workoutIntensity} ${styles[workout.intensity]}`}>
            <ImagePublicFileId fileId={intensity.filterActiveIconPublicFileId} className={styles.intensityIcon} />
            <span>{intensity.label} Intensity</span>
          </div>
        )}
        {duration && (
          <div>
            <ClockSvgIcon width={16} height={16} />
            <span>{duration} workout</span>
          </div>
        )}
        {equipmentPiecesLabel && (
          <div>
            <EquipmentSvgIcon width={20} height={9.5} />
            {equipmentPiecesLabel}
          </div>
        )}
      </div>
      <p className={styles.workoutDescription}>{description}</p>
      <div className={styles.workoutLinks}>
        {/*
         * Presence, not `!== null` (GH-919) — same fix as `workout-detail-action.tsx`, and for
         * the same reason: a blank `playlist_url` rendered a dead icon. This popup opens from
         * Home, the workout lists, favourites, completed workouts and the programme and series
         * overviews, so the icon disappearing on those workouts is visible in more places than
         * the ticket's title suggests.
         *
         * `ProgrammeSpotifySvgIcon` stays — the glyphs are deliberately NOT unified here
         * (design.md Decision 3).
         *
         * ⚠️ This row is `justify-content: space-between` over exactly three children, so on a
         * workout with no playlist "Start workout" is now flush against the left padding
         * instead of sitting between the two icons. Left alone knowingly: GH-907 is scoped to
         * visibility, and re-centring the CTA is a styling change to a shared popup that no
         * one has asked for. If it reads badly in review, the fix is a `justify-content`
         * change here, not putting the dead anchor back.
         */}
        {workout.playlistUrl ? (
          <a href={workout.playlistUrl} target="_blank" rel="noreferrer">
            <ProgrammeSpotifySvgIcon />
          </a>
        ) : null}
        <button type="button" onClick={handleStartWorkout} className={styles.startWorkoutBtn}>
          Start workout
        </button>
        <div onClick={handleFavoritePress} className={styles.favoriteIconContainer}>
          <img src={isFavorite ? HeartIconSelected : HeartIcon} alt="Favorite" />
        </div>
      </div>
    </div>
  )
}

const getIntensity = (intensities: Intensity[] | undefined, intensityId: string | undefined): Intensity | undefined => {
  if (!intensities || !intensityId) {
    return undefined
  }
  return intensities.find((i) => i.id === intensityId)
}
